import React, { useState, useEffect, useCallback } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Button, Badge, Input, Select, Textarea, useToast } from '../../components/common';
import { useGroup } from '../../contexts/GroupContext';
import api from '../../services/api';
import './MyAppsTab.css';
import '../../styles/PortalPage.css';
import './CapabilitiesTab.css';
import CapabilityRunner, { cannotSend } from './CapabilityRunner';
import PanelActions from '../../components/dev/PanelActions';
import CapabilityEditor, { HEALTH_TEMPLATE } from './CapabilityEditor';

const EMPTY_DOC = {
  title: '',
  status: 'draft',
  spec_url: '',
  overview_md: '',
  auth_schemes: {},
  endpoints: [],
};

function CapabilitiesTab({ groupId = null, appId: fixedAppId = null }) {
  // Embedded in an app's page there is nothing to choose — the picker was a
  // second copy of the one on My Applications.
  const toast = useToast();
  const { activeGroupId } = useGroup();
  const effectiveGroupId = groupId || activeGroupId;

  const [apps, setApps] = useState([]);
  const [appsLoading, setAppsLoading] = useState(false);
  const [selectedAppId, setSelectedAppId] = useState(fixedAppId || '');

  const [docLoading, setDocLoading] = useState(false);
  const [hasDoc, setHasDoc] = useState(false);
  const [versions, setVersions] = useState([]);

  // Editor form. auth_schemes / endpoints are edited as raw JSON text.
  const [form, setForm] = useState({ ...EMPTY_DOC });
  const [saving, setSaving] = useState(false);
  // Owned by the reader, not derived from the data. Closed to begin with: the
  // form is long, and the page is for reading the API. The panel head's
  // "Edit the document" opens it, as does adding a health endpoint.
  const [editorOpen, setEditorOpen] = useState(false);

  /* Adding must leave you looking at the field you now have to fill in. */
  const addHealth = useCallback(() => {
    setForm(f => ({
      ...f,
      endpoints: [{ ...HEALTH_TEMPLATE }, ...(f.endpoints || [])],
    }));
    setEditorOpen(true);
    // The editor is below the fold on a short viewport; landing on a collapsed
    // drawer you cannot see is how the first version lost people.
    setTimeout(() => {
      document.querySelector('.ce-ep input')?.focus();
      document.querySelector('.cap-adv')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 60);
  }, []);

  // --- Load the developer's apps (same call as MyAppsTab / DeveloperDashboard) ---
  const loadApps = useCallback(async () => {
    setAppsLoading(true);
    try {
      const url = effectiveGroupId
        ? `/third-party/my-apps?group_id=${effectiveGroupId}`
        : '/third-party/my-apps';
      const res = await api.request(url).catch(() => ({ apps: [] }));
      setApps(res.apps || []);
    } catch {
      setApps([]);
    } finally {
      setAppsLoading(false);
    }
  }, [effectiveGroupId]);

  useEffect(() => { loadApps(); }, [loadApps]);

  // --- Load the selected app's capability doc + versions ---
  const applyDoc = useCallback((doc) => {
    setForm({
      title: doc.title || '',
      status: doc.status || 'draft',
      spec_url: doc.spec_url || '',
      overview_md: doc.overview_md || '',
      auth_schemes: doc.auth_schemes || {},
      endpoints: doc.endpoints || [],
    });
  }, []);

  const loadDoc = useCallback(async (appId) => {
    if (!appId) return;
    setDocLoading(true);
    try {
      const [doc, versionsRes] = await Promise.all([
        api.getCapabilities(appId).catch((err) => {
          if (err?.status === 404) return null; // no doc yet
          throw err;
        }),
        api.getCapabilityVersions(appId).catch(() => ({ versions: [] })),
      ]);
      setVersions(versionsRes.versions || []);
      if (doc) {
        setHasDoc(true);
        applyDoc(doc);
      } else {
        setHasDoc(false);
        applyDoc(EMPTY_DOC);
      }
    } catch (err) {
      toast.error(err.message || 'Failed to load capability doc');
    } finally {
      setDocLoading(false);
    }
  }, [applyDoc, toast]);

  useEffect(() => {
    if (fixedAppId) loadDoc(fixedAppId);
  }, [fixedAppId, loadDoc]);

  const handleSelectApp = (appId) => {
    setSelectedAppId(appId);
    setHasDoc(false);
    setVersions([]);
    applyDoc(EMPTY_DOC);
    if (appId) loadDoc(appId);
  };

  // --- Save (POST snapshots a new version) ---
  const handleSave = async () => {
    if (!selectedAppId) return;

    // The endpoint array is the source of truth now, so there is nothing to
    // parse and nothing to fail on. What used to be "Endpoints JSON is invalid"
    // is caught per field, next to the field, while you type.
    const payload = {
      title: form.title,
      status: form.status,
      spec_url: form.spec_url,
      overview_md: form.overview_md,
      auth_schemes: form.auth_schemes || {},
      endpoints: form.endpoints || [],
    };

    setSaving(true);
    try {
      const saved = await api.saveCapabilities(selectedAppId, payload);
      toast.success(`Capabilities saved — version ${saved.version}.`);
      setHasDoc(true);
      applyDoc(saved);
      const versionsRes = await api.getCapabilityVersions(selectedAppId).catch(() => ({ versions: [] }));
      setVersions(versionsRes.versions || []);
    } catch (err) {
      toast.error(err.message || 'Failed to save capabilities');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteVersion = async (version) => {
    if (!selectedAppId) return;
    try {
      await api.deleteCapabilityVersion(selectedAppId, version);
      toast.success(`Version ${version} deleted.`);
      await loadDoc(selectedAppId);
    } catch (err) {
      toast.error(err.message || 'Failed to delete version');
    }
  };

  const ready = !!selectedAppId && !docLoading;
  const endpoints = form.endpoints || [];

  return (
    <div className={fixedAppId ? 'cap' : 'portal-page cap'}>
      {!fixedAppId && (
        <div className="myapps__head">
          <h2 className="myapps__title">API Capability Docs</h2>
        </div>
      )}
      {ready && (
        <PanelActions>
          <Button variant="secondary" onClick={() => setEditorOpen(o => !o)} aria-expanded={editorOpen}>
            {editorOpen ? 'Close the editor' : 'Edit the document'}
          </Button>
        </PanelActions>
      )}
      <p className="cap__lede">
        The endpoints this app offers other apps and agents, and how to authenticate to each.
      </p>

      {!fixedAppId && (
        <div className="myapps-edit__field" style={{ maxWidth: 480 }}>
          <label className="myapps-edit__label">Application</label>
          <Select
            value={selectedAppId}
            onChange={(e) => handleSelectApp(e.target.value)}
            disabled={appsLoading}
          >
            <option value="">{appsLoading ? 'Loading apps…' : '— select an application —'}</option>
            {apps.map((app) => (
              <option key={app.id} value={app.id}>{app.name}</option>
            ))}
          </Select>
        </div>
      )}

      {!selectedAppId && (
        <div className="myapps__empty" style={{ marginTop: 16 }}>
          Select an application to manage its API capability docs.
        </div>
      )}
      {selectedAppId && docLoading && <p className="myapps__loading">Loading capability doc…</p>}

      {ready && editorOpen && (
        <section className="cap-adv" aria-label="Edit the document">
          <div className="cap-band__top">
            <h3 className="appfields__title">Edit the document</h3>
            <Badge variant={hasDoc ? 'success' : 'default'}>
              {hasDoc ? 'Existing doc' : 'No doc yet'}
            </Badge>
            {hasDoc && form.status && (
              <Badge variant={form.status === 'published' ? 'success' : 'warning'}>
                {form.status === 'published' ? 'Published' : 'Draft'}
              </Badge>
            )}
          </div>
          <div className="cap-adv__body">
            <Input
              label="Title"
              type="text"
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              placeholder="e.g. Acme Public API"
            />

            <div className="myapps-edit__field">
              <label className="myapps-edit__label">Status</label>
              <Select
                value={form.status}
                onChange={(e) => setForm({ ...form, status: e.target.value })}
                options={[
                  { value: 'draft', label: 'Draft' },
                  { value: 'published', label: 'Published' },
                ]}
              />
            </div>

            <Input
              label="Spec URL"
              type="url"
              value={form.spec_url}
              onChange={(e) => setForm({ ...form, spec_url: e.target.value })}
              placeholder="https://example.com/openapi.json"
            />

            <Textarea
              label="Overview (Markdown)"
              value={form.overview_md}
              onChange={(e) => setForm({ ...form, overview_md: e.target.value })}
              placeholder={'# Overview\nWhat this API does and how to authenticate.'}
              rows={6}
            />
            {form.overview_md && form.overview_md.trim() && (
              <div className="cap-md-preview">
                <div className="cap-md-preview__label">Preview</div>
                <div className="cap-md-preview__body">
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>{form.overview_md}</ReactMarkdown>
                </div>
              </div>
            )}

            <CapabilityEditor
              endpoints={form.endpoints}
              schemes={form.auth_schemes}
              onEndpoints={(next) => setForm(f => ({ ...f, endpoints: next }))}
              onSchemes={(auth_schemes) => setForm(f => ({ ...f, auth_schemes }))}
            />

            {/* An ordinary row at the end of the form: sticky needs an opaque
                background, and that background was a stray white slab. */}
            <div className="cap-save">
              <Button variant="primary" size="sm" onClick={handleSave} disabled={saving}>
                {saving ? 'Saving…' : 'Save'}
              </Button>
              <span className="cap-save__hint">
                Saving snapshots a new version. A draft stays private to you.
              </span>
              <button type="button" className="cap-act" onClick={() => setEditorOpen(false)}>Close</button>
            </div>
          </div>
        </section>
      )}

      {ready && (endpoints.length === 0 ? (
        <CapabilitiesEmpty onAddHealth={addHealth} />
      ) : (
        <>
          <HealthStrip appId={selectedAppId} endpoints={endpoints} schemes={form.auth_schemes} onAddHealth={addHealth} />
          <EndpointList endpoints={endpoints} schemes={form.auth_schemes} doc={form} />
        </>
      ))}

      {ready && (
        <section className="cap-versions">
          <h3 className="appfields__title">Versions</h3>
          {versions.length === 0 ? (
            <p className="appfields__note">No versions yet. Saving the document makes the first.</p>
          ) : (
            <div className="cap-rows">
              {versions.map((v) => (
                <div className="cap-row" key={v.version}>
                  <span className="cap-row__k">v{v.version}</span>
                  <span className="cap-row__v">
                    {v.status === 'published' ? 'Published' : 'Draft'}
                    {v.title && <> · {v.title}</>}
                    {v.created_at && <> · {new Date(v.created_at).toLocaleDateString()}</>}
                  </span>
                  <button type="button" className="cap-act cap-act--danger" onClick={() => handleDeleteVersion(v.version)}>
                    Delete
                  </button>
                </div>
              ))}
            </div>
          )}
        </section>
      )}
    </div>
  );
}


/* ── the live surface ───────────────────────────────────────────────────────
 *
 * The tab used to be two textareas of raw JSON and nothing in the platform ever
 * called the URLs it collected. These pieces are the difference between a
 * declaration and a probe: what state the app is in, every endpoint runnable
 * from here, and exactly what an agent receives.
 */

/** The one endpoint the platform probes, if the doc names one. */
function healthEndpoint(endpoints) {
  return (endpoints || []).find(e => e && e.health === true) || null;
}

/** 30 days of probes, one bar per day. */
function Ribbon({ days }) {
  return (
    <div className="cap-ribbon">
      <div className="cap-ribbon__bars">
        {days.map(d => (
          <i
            key={d.date}
            className={`is-${d.state || 'gap'}`}
            title={d.state
              ? `${d.date}: ${d.probes - d.failed}/${d.probes} probes passed`
              : `${d.date}: not probed`}
          />
        ))}
      </div>
      <div className="cap-ribbon__scale"><span>30 days ago</span><span>today</span></div>
    </div>
  );
}

function HealthStrip({ appId, endpoints, schemes, onAddHealth }) {
  const health = healthEndpoint(endpoints);
  const [open, setOpen] = useState(false);
  /* The result of a check has to outlive the runner that produced it, or
     pressing Hide throws away the 200 you just saw. */
  const [last, setLast] = useState(null);
  const [probe, setProbe] = useState(null);
  const [checking, setChecking] = useState(false);
  const [refused, setRefused] = useState(null);

  useEffect(() => {
    if (!appId) return;
    api.request(`/third-party/${appId}/capabilities/health`)
      .then(setProbe)
      .catch(() => setProbe(null));
  }, [appId]);

  if (!health) {
    return (
      <section className="cap-health">
        <h3 className="appfields__title">Health</h3>
        <div className="cap-rows">
          <div className="cap-row">
            <span className="cap-row__k">State</span>
            <span className="cap-row__v">
              <span className="cap-state is-unknown"><i className="cap-dot bg-unknown" />No health endpoint</span>
              <span className="cap-row__sub">
                Mark one GET endpoint <code>health: true</code> with <code>auth: none</code> and ofSelf can tell whether this app is up.
              </span>
            </span>
            <button type="button" className="cap-act" onClick={onAddHealth}>Add one</button>
          </div>
        </div>
      </section>
    );
  }

  /* Ask Paradigm to probe, rather than probing from here: the browser check
     answers whether the app answers YOU, and records nothing. */
  const checkNow = async () => {
    setChecking(true);
    setRefused(null);
    try {
      const res = await api.request(`/third-party/${appId}/capabilities/health/check`, { method: 'POST' });
      setProbe(res);
      // A refusal is a result, not an error: the guard will not call a private
      // address, and that is the answer for every app still on localhost.
      if (res?.probe && !res.probe.ok) setRefused(res.probe.error);
    } catch (err) {
      setRefused(err.message || 'The check could not run.');
    }
    setChecking(false);
  };

  const unusable = cannotSend(health);
  /* The platform's own verdict wins when it has one — it probes from the
     network that matters. A browser check is a fallback, labelled as one. */
  const latest = probe?.latest;
  const state = latest
    ? (latest.ok ? 'ok' : 'down')
    : last
      ? (last.blocked ? 'down' : (last.graded ? (last.graded.ok ? 'ok' : 'down') : (last.ok ? 'ok' : 'down')))
      : 'unknown';
  const WORD = { ok: 'Answering', down: 'Down', unknown: 'Not probed yet' };
  const method = String(health.method || 'GET').toUpperCase();

  return (
    <section className="cap-health">
      <h3 className="appfields__title">Health</h3>
      <div className="cap-rows">
        <div className="cap-row">
          <span className="cap-row__k">State</span>
          <span className="cap-row__v">
            <span className={`cap-state is-${state}`}><i className={`cap-dot bg-${state}`} />{WORD[state]}</span>
            <span className="cap-row__sub">
              {latest ? (
                <>
                  {latest.ok ? `${latest.status} in ${latest.ms} ms` : (latest.error || 'failed')}
                  {', '}checked by ofSelf {timeAgo(latest.checked_at)}
                </>
              ) : last ? (
                <>
                  {last.blocked
                    ? 'your browser could not reach it'
                    : <>{last.status} in {last.ms} ms{last.graded && (last.graded.ok
                        ? ', matching what it says to expect'
                        : `, but ${last.graded.failures.join('; ')}`)}</>}
                  {', '}checked from your browser, not by ofSelf
                </>
              ) : probe?.why_not_probing || (unusable
                ? 'The platform cannot probe an endpoint it has no absolute URL for.'
                : 'Declared, but nothing has probed it yet.')}
            </span>
          </span>
          <button type="button" className="cap-act" onClick={checkNow} disabled={checking}>
            {checking ? 'Checking…' : 'Check now'}
          </button>
        </div>
        <div className="cap-row">
          <span className="cap-row__k">Endpoint</span>
          <span className="cap-row__v">
            <span className="cap-verb">{method}</span>
            {health.url
              ? <code className="cap-url">{health.url}</code>
              : <em className="cap-health__nourl">no URL yet</em>}
          </span>
          <button type="button" className="cap-act" onClick={() => setOpen(o => !o)} aria-expanded={open}>
            {open ? 'Hide' : 'Check from this browser'}
          </button>
        </div>
        {probe?.probes > 0 && probe.uptime_pct != null && (
          <div className="cap-row">
            <span className="cap-row__k">Uptime, 30 days</span>
            <span className="cap-row__v">{probe.uptime_pct}% <span className="cap-row__sub">over {probe.probes} probe{probe.probes === 1 ? '' : 's'}</span></span>
            <span />
          </div>
        )}
        {probe?.probes > 0 && probe.median_ms != null && (
          <div className="cap-row">
            <span className="cap-row__k">Median response</span>
            <span className="cap-row__v">{probe.median_ms} ms</span>
            <span />
          </div>
        )}
      </div>

      {probe?.days?.some(d => d.state) && <Ribbon days={probe.days} />}

      {refused && (
        <p className="cap-health__refused">
          <b>ofSelf could not probe it.</b> {refused}
        </p>
      )}

      {open && (
        <div className="cap-health__runner">
          <p className="cap-run__note">
            Your browser calls it directly, from your network — useful for localhost or anything
            not yet deployed. Nothing is recorded: that it answered <em>you</em> says nothing about
            whether the app is up.
          </p>
          <CapabilityRunner endpoint={health} schemes={schemes} onResult={setLast} />
        </div>
      )}
    </section>
  );
}

/** "4m ago" — a probe timestamp is only ever read as a distance from now. */
function timeAgo(iso) {
  const s = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 1000));
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.round(s / 60)}m ago`;
  if (s < 86400) return `${Math.round(s / 3600)}h ago`;
  return `${Math.round(s / 86400)}d ago`;
}

function EndpointRow({ endpoint, schemes }) {
  const [open, setOpen] = useState(false);
  const method = String(endpoint.method || 'GET').toUpperCase();
  let path = endpoint.url || '';
  try { path = new URL(endpoint.url).pathname; } catch { /* a relative or malformed url shows whole */ }
  const toggle = () => setOpen(o => !o);

  return (
    <div className={`cap-ep${open ? ' is-open' : ''}`}>
      <div
        className="cap-tbl__r"
        onClick={toggle}
        onKeyDown={e => (e.key === 'Enter' || e.key === ' ') && (e.preventDefault(), toggle())}
        role="button"
        tabIndex={0}
        aria-expanded={open}
      >
        <span><span className="cap-verb">{method}</span></span>
        <span className="cap-ep__path">
          <code>{path || '—'}</code>
          {endpoint.health && <span className="cap-ep__tag">health</span>}
        </span>
        <span className="cap-ep__name">
          {endpoint.name}
          {!endpoint.expect && (
            <span className="cap-ep__noexpect" title="Declares no `expect`, so nothing can grade it — not the CLI, not CI, not an agent">
              no expect
            </span>
          )}
        </span>
        <span className="cap-ep__auth">{endpoint.auth && endpoint.auth !== 'none' ? String(endpoint.auth) : 'none'}</span>
      </div>
      {open && <div className="cap-ep__body"><CapabilityRunner endpoint={endpoint} schemes={schemes} /></div>}
    </div>
  );
}

function EndpointList({ endpoints, schemes, doc }) {
  const graded = endpoints.filter(e => e && e.expect).length;
  const ungraded = endpoints.length - graded;
  return (
    <section className="cap-endpoints">
      <h3 className="appfields__title">Endpoints</h3>
      <p className="appfields__note">What other apps and agents can call. Select one to try it from here.</p>
      <div className="cap-tbl" role="table" aria-label="Endpoints">
        <div className="cap-tbl__h" role="row">
          <span>Method</span><span>Path</span><span>What it does</span><span>Auth</span>
        </div>
        {endpoints.map((e, i) => <EndpointRow key={`${e.name}-${i}`} endpoint={e} schemes={schemes} />)}
      </div>
      <p className="cap-foot">
        {endpoints.length} declared · {graded} can be graded
        {ungraded > 0 && ` · ${ungraded} declare${ungraded === 1 ? 's' : ''} no expect, so nothing can check ${ungraded === 1 ? 'its' : 'their'} answer`}
      </p>
      <AgentView doc={doc} />
    </section>
  );
}

/** Exactly what a first-party agent receives, because "the agent doesn't know
 *  what to do" is only debuggable if you can see what it was handed. */
function AgentView({ doc }) {
  const payload = {
    title: doc.title || null,
    status: doc.status,
    auth_schemes: doc.auth_schemes || {},
    endpoints: (doc.endpoints || []).map(e => ({
      name: e.name, method: e.method, url: e.url, auth: e.auth ?? 'none',
      params: e.params || [], expect: e.expect || null, health: !!e.health,
    })),
  };
  return (
    <details className="cap-agentview">
      <summary>What an agent reads</summary>
      <p className="cap-run__note">
        An endpoint with no <code>expect</code> is a menu with no prices: an agent can see it
        exists and cannot tell whether the answer it got back was the right one.
      </p>
      <pre className="cap-agent">{JSON.stringify(payload, null, 2)}</pre>
    </details>
  );
}

/** The screen an app with nothing declared gets: one band, one thing to do. */
function CapabilitiesEmpty({ onAddHealth }) {
  return (
    <section className="cap-empty">
      <h3 className="appfields__title">No API published yet</h3>
      <p className="appfields__note">
        It&apos;s optional — an app with no external API is fine. If it has one, start with a health
        endpoint: a GET with <code>auth: none</code> that ofSelf can probe to tell whether the app is up.
      </p>
      <div className="cap-empty__cta">
        <Button onClick={onAddHealth}>Add a health endpoint</Button>
        <span>or scaffold it with <code>paradigm capabilities init</code></span>
      </div>
    </section>
  );
}

export default CapabilitiesTab;
